import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { MessageSquare } from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';

export function Hero() {
  const [content, setContent] = useState({
    hero_headline: 'Relax at Walnut Farm',
    hero_description: 'Experience a serene farmhouse getaway with a pool and open kitchen for unforgettable memories.',
    hero_cta: 'Enquire on WhatsApp'
  });

  useEffect(() => {
    const fetchContent = async () => {
      const { data } = await supabase
        .from('page_content')
        .select('key, content')
        .in('key', ['hero_headline', 'hero_description', 'hero_cta']);
      
      if (data) {
        const newContent = { ...content };
        data.forEach(item => {
          if (item.key in newContent) {
            newContent[item.key as keyof typeof content] = item.content;
          }
        });
        setContent(newContent);
      }
    };
    fetchContent();
  }, []);

  const whatsappLink = `https://wa.me/918900992020?text=${encodeURIComponent(
    "Hello Walnut Farm Estate, I would like to enquire about availability and booking. Please share the available dates and stay details."
  )}`;

  return (
    <section id="home" className="relative h-screen w-full overflow-hidden flex items-center justify-center">
      <motion.div 
        initial={{ scale: 1.1 }}
        animate={{ scale: 1 }}
        transition={{ duration: 2.5, ease: "easeOut" }}
        className="absolute inset-0 z-0"
      >
        <img
          src="/assets/farmhouse-night-pool.jpg"
          alt="Walnut Farm"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-black/40 mix-blend-multiply" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/80" />
      </motion.div>

      <div className="relative z-10 w-full max-w-screen-2xl mx-auto px-4 md:px-6 text-center flex flex-col items-center justify-center h-full pt-20">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
          className="w-full"
        >
          <h1 className="text-[clamp(2.5rem,8vw,8rem)] font-serif mb-4 md:mb-8 leading-[1.2] tracking-normal font-semibold !text-[#FFFFFF]">
            <span className="block italic">{content.hero_headline}</span>
          </h1>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.5, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="text-base md:text-xl font-medium w-[90%] max-w-3xl mx-auto mb-8 md:mb-16 !text-[#FFF4DC] drop-shadow-[0_2px_6px_rgba(0,0,0,0.8)] font-clean"
          style={{ color: '#FFF4DC', textShadow: '0 2px 6px rgba(0,0,0,0.8)' }}
        >
          {content.hero_description}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.5, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="flex items-center justify-center w-full px-4"
        >
          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "group relative flex items-center justify-center space-x-4 px-6 md:px-12 py-4 md:py-5 rounded-full font-bold text-[16px] md:text-[18px] overflow-hidden transition-all duration-500 font-clean uppercase tracking-wider",
              "bg-black/60 backdrop-blur-xl border border-[#B8863B]/40 text-white",
              "shadow-[0_0_30px_rgba(23,107,91,0.3)] hover:shadow-[0_0_40px_rgba(184,134,59,0.4)]",
              "hover:border-[#D4AF37] hover:scale-105",
              "w-[85%] max-w-[380px] md:w-auto"
            )}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-[#176B5B]/30 via-[#B8863B]/20 to-[#176B5B]/30 opacity-60 group-hover:opacity-90 transition-opacity duration-500" />
            
            <MessageSquare className="w-4 h-4 relative z-10 text-[#B8863B] transition-transform duration-500 group-hover:scale-110" />
            <span className="relative z-10 bg-gradient-to-r from-white via-[#F3EFE6] to-white bg-clip-text text-transparent">
              {content.hero_cta}
            </span>
            
            <div className="absolute -inset-[100%] group-hover:animate-[spin_4s_linear_infinite] opacity-0 group-hover:opacity-30 transition-opacity bg-[conic-gradient(from_0deg,transparent_0deg,transparent_120deg,#B8863B_180deg,transparent_240deg,transparent_360deg)]" />
          </a>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 2 }}
        className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center space-y-4 opacity-40 group cursor-pointer"
        onClick={() => document.getElementById('farm')?.scrollIntoView({ behavior: 'smooth' })}
      >
        <span className="text-[14px] uppercase tracking-[0.2em] font-clean !text-white">Discover</span>
        <div className="w-[1px] h-12 bg-gradient-to-b from-white to-transparent" />
      </motion.div>
    </section>
  );
}
